import React, { useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { http } from 'app/lib/axios';
import { pickLoanFeeDocument } from 'app/lib/pick-loan-fee-document';
import { LoanFeeDocumentPreview } from './loan-fee-document-preview';

export function LoanFeeDocumentField({
  value,
  onChange,
}: {
  value?: string | null;
  onChange: (url: string | null) => void;
}) {
  const { t } = useTranslation('loanFee');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = async () => {
    setError(null);
    try {
      const form = await pickLoanFeeDocument();
      if (!form) return;
      setUploading(true);
      const { data } = await http.post<{ url: string }>('/listings/loan-fee-document', form);
      onChange(data.url);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error && uploadError.message === 'invalidDocument'
          ? t('document.invalid')
          : t('document.uploadError'),
      );
    }
  };
  return (
    <Column gap="spacing.general.x2">
      {!value ? <Text variant="bodyBaseMedium">{t('document.label')}</Text> : null}
      {value ? <LoanFeeDocumentPreview url={value} /> : null}
      <Row gap="spacing.general.x4" flexWrap="wrap">
        <Button
          variant={value ? 'plain' : 'secondary'}
          disabled={uploading}
          onPress={() => void upload().finally(() => setUploading(false))}
        >
          <Text variant="bodyBaseMedium">{t(value ? 'document.replace' : 'document.choose')}</Text>
        </Button>
        {value ? (
          <Button variant="plain" disabled={uploading} onPress={() => onChange(null)}>
            <Text variant="bodyBaseMedium">{t('document.remove')}</Text>
          </Button>
        ) : null}
      </Row>
      <Text variant="bodySmall">{t('document.help')}</Text>
      {error ? <Text accessibilityRole="alert">{error}</Text> : null}
      <Modal open={uploading} transparent>
        <Modal.Card>
          <ActivityIndicator />
          <Text>{t('document.uploading')}</Text>
        </Modal.Card>
      </Modal>
    </Column>
  );
}
